'use client';

import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { TenantLogo, DEFAULT_BRANDING, type BrandingConfig } from '@/features/branding';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { UserMenu } from '@/shared/layout/UserMenu';

/**
 * App chrome for the token-based surfaces (landing, create, notes). The brand
 * mark lives here — top-left, sized up — not inside the content card, so every
 * page shares one masthead and the content below can breathe.
 */
export function AppHeader({
  branding = DEFAULT_BRANDING,
  actions,
  className,
  homeHref = '/',
  showAccountMenu = false,
}: {
  branding?: BrandingConfig;
  actions?: React.ReactNode;
  className?: string;
  /** Where the brand mark links. Pass `null` on public surfaces (join/lobby)
   *  where sending a lead to the salesperson home would be wrong. */
  homeHref?: string | null;
  /** Explicit opt-in only — must stay `false` on any surface a lead/guest
   *  might see (UserMenu itself resolves to nothing if SSO never bootstraps,
   *  but the default here is false so that's never relied upon). */
  showAccountMenu?: boolean;
}) {
  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full border-b border-[var(--border)]',
        'bg-[color-mix(in_srgb,var(--bg)_82%,transparent)] backdrop-blur-xl',
        className,
      )}
    >
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        {homeHref ? (
          <Link
            href={homeHref}
            aria-label={`${branding.name} home`}
            className="flex items-center gap-2.5 rounded-[var(--radius)] outline-none transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]"
          >
            <TenantLogo branding={branding} size="nav" priority />
          </Link>
        ) : (
          <span className="flex items-center gap-2.5" aria-label={branding.name}>
            <TenantLogo branding={branding} size="nav" priority />
          </span>
        )}
        <div className="flex items-center gap-2">
          {actions}
          {showAccountMenu && <UserMenu />}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
